/**
 * One seat on the table. SPEC.md §8, §16.5, §9.3.
 *
 * The score is the dominant element and always carries its sign, so the value
 * survives greyscale and colour blindness. §8.1, §19
 *
 * The own-seat marker is a chip and a border, never a bigger card: seat geometry
 * has to stay stable for the ring to read like the real table. §8.2
 *
 * A seat is no longer a control. It used to be the host's one tap per hand —
 * "who won?" — and that question stopped being asked: the winner is whoever
 * reports nothing, so there is nobody to designate and nothing for a tap to do.
 * What is left is what the seat was always for: showing where somebody sits and
 * letting them be dragged somewhere else. §3.1, §9.3
 *
 * An offline player keeps their seat and gains an "Offline 43s" line. They leave
 * the table only when the host removes them. §8.5, §16.5
 */

import type { CSSProperties } from 'react';
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import { describeScore, truncateName, type PlayerView } from '@card-counter/shared';
import { Avatar } from '@/components/ui/Avatar';
import { OfflineFor } from '@/components/ui/OfflineFor';
import { SignedScore } from '@/components/ui/SignedScore';
import { useConnectionState } from '@/stores/connection-store';
import {
  useHandProgress,
  useHandEntryOf,
  useIsDealer,
  useMyPlayerId,
  useTotalFor,
} from '@/stores/session-store';

/** Ring labels are shorter than the stored cap; the full name lives in history. §8.6 */
const RING_NAME_MAX = 10;
const CROWDED_RING_NAME_MAX = 7;

/** How long after a drag a click on the same seat is ignored. §9.3 */
const TAP_SUPPRESSION_MS = 350;

let tapSuppressedUntil = 0;

/**
 * Called by TableRing when a drag starts or ends. A long-press that turns into a
 * drag still emits a click on release in several browsers, and that click must
 * not reach whatever is behind the seat. §9.3
 */
export function suppressSeatTap(): void {
  tapSuppressedUntil = Date.now() + TAP_SUPPRESSION_MS;
}

export function seatTapSuppressed(): boolean {
  return Date.now() < tapSuppressedUntil;
}

// --- Connection dot ----------------------------------------------------------

type SeatConnection = 'connected' | 'reconnecting' | 'offline';

/** ● connected, ◌ reconnecting, ○ offline. Subtle by design. §8.5 */
const DOT_GLYPH: Record<SeatConnection, string> = {
  connected: '●',
  reconnecting: '◌',
  offline: '○',
};

const DOT_CLASS: Record<SeatConnection, string> = {
  connected: 'text-positive/70',
  reconnecting: 'text-warn',
  offline: 'text-ink-faint',
};

export interface PlayerSeatProps {
  player: PlayerView;
  /** Avatar scale from the shared avatarScaleForCount, so a full table never overlaps. §45 */
  scale?: number;
  /** This seat is the drag source. §9.3 */
  dragging?: boolean;
  /** A drag is hovering this seat and would swap with it. §9.4 */
  dropTarget?: boolean;
  /**
   * dnd-kit wiring, supplied by TableRing only when the viewer may move this
   * seat: your own avatar, or any avatar if you are the host. §9.3
   */
  dragHandle?: {
    ref: (element: HTMLElement | null) => void;
    listeners: DraggableSyntheticListeners;
    attributes: DraggableAttributes;
  };
}

export function PlayerSeat({
  player,
  scale = 1,
  dragging = false,
  dropTarget = false,
  dragHandle,
}: PlayerSeatProps) {
  const myPlayerId = useMyPlayerId();
  const total = useTotalFor(player.id);
  const connectionState = useConnectionState();
  const entry = useHandEntryOf(player.id);
  const { winnerPlayerId } = useHandProgress();
  const isDealer = useIsDealer(player.id);

  const isMe = player.id === myPlayerId;
  const isWinner = winnerPlayerId === player.id;

  /**
   * Presence is what the server observes about other players; for your own seat
   * the honest signal is this device's own transport state. §8.5, §16.2
   */
  const connection: SeatConnection = isMe
    ? connectionState === 'connected'
      ? 'connected'
      : connectionState === 'offline'
        ? 'offline'
        : 'reconnecting'
    : player.presence === 'offline'
      ? 'offline'
      : 'connected';

  const avatarStyle: CSSProperties = {
    width: `calc(var(--avatar-size) * ${String(scale)})`,
    height: `calc(var(--avatar-size) * ${String(scale)})`,
  };

  const label = `${player.name}, ${describeScore(total)}${
    isWinner ? ', winning this hand' : entry ? `, reported ${String(entry.amount)}` : ''
  }${player.presence === 'offline' && !isMe ? ', offline' : ''}${
    // Last, so it never comes between a name and the score every other surface
    // announces straight after it. §19
    isDealer ? ', dealing this hand' : ''
  }`;

  return (
    <div
      ref={dragHandle?.ref}
      {...(dragHandle?.listeners ?? {})}
      onContextMenu={(event) => {
        // A long press must start a drag, not open the OS menu. §9.3
        if (dragHandle) event.preventDefault();
      }}
      className={[
        /**
         * `touch-action: none` on a seat that can actually be dragged, and only
         * on that seat. §9.3
         *
         * The ring lives inside a scrolling sheet. Under `touch-manipulation`
         * the browser is still free to treat a finger on an avatar as a pan, so
         * it scrolls the sheet under the drag — the avatar goes one way, the
         * whole panel goes the other, and the drop lands nowhere near where it
         * looked. Pointer events keep firing throughout, which is why dnd-kit
         * cannot notice and fix this itself.
         *
         * Scoped to the handle rather than the ring so the sheet can still be
         * scrolled from anywhere else in it, including the felt between seats.
         */
        'flex w-max flex-col items-center gap-0.5 rounded-card px-1 py-0.5 select-none',
        dragHandle ? 'touch-none' : 'touch-manipulation',
        'transition-[opacity,transform] duration-200 ease-out-soft',
        dragging ? 'cursor-grabbing' : dragHandle ? 'cursor-grab' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span className="relative grid shrink-0 place-items-center" style={avatarStyle}>
        <span
          style={avatarStyle}
          className={[
            'grid place-items-center rounded-full border bg-raised leading-none',
            'transition-[transform,border-color,box-shadow] duration-200 ease-out-soft',
            dropTarget ? 'border-accent ring-2 ring-accent' : '',
            !dropTarget && isMe ? 'border-accent' : '',
            !dropTarget && !isMe ? 'border-line' : '',
            connection === 'offline' ? 'opacity-60 grayscale' : '',
            dragging ? 'scale-110 shadow-card' : '',
            // Whose deal it is. Yielded while the seat is being dragged or
            // dropped onto, because all three are box-shadows on one avatar and
            // the two that answer a finger are the ones that matter then. §9.3
            isDealer && !dragging && !dropTarget ? 'dealer-glow' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {/* Inset from the plate's rim: the artwork already fills the circle
              it was rendered into, so at full size it would sit on the border
              rather than inside it. §46 */}
          <Avatar avatarKey={player.avatarKey} className="size-[86%]" />
        </span>

        {/* The crown goes to whoever the hand is currently going to — which is
            read out of everyone else's reports, not designated. §3.1, §8.3 */}
        {isWinner ? (
          <span
            aria-hidden="true"
            className="absolute -top-1 -right-1 grid size-[1.35em] place-items-center rounded-full bg-surface text-[0.75em] leading-none shadow-card"
          >
            👑
          </span>
        ) : entry ? (
          <span
            aria-hidden="true"
            data-numeric
            className="absolute -right-1 -bottom-1 grid min-w-[1.5em] place-items-center rounded-full border border-line bg-surface px-1 text-[0.6em] leading-tight font-bold text-ink-muted shadow-card"
          >
            {entry.shape === 'TO_EVERYONE' ? `×${String(entry.amount)}` : entry.amount}
          </span>
        ) : null}
      </span>

      {/* Re-keyed on the committed total so the change ticks in. §20.4
          Silent: the whole seat is announced through `label`, sign included. */}
      <SignedScore
        key={total}
        value={total}
        silent
        className="animate-tick-in text-score leading-none font-bold"
      />

      <span className="flex max-w-[7.5rem] items-center gap-1">
        {isMe ? (
          // §8.2 — a marker, not a different size.
          <span className="text-[0.625rem] font-bold tracking-[0.12em] text-accent uppercase">
            You
          </span>
        ) : (
          <span className="truncate text-xs text-ink-muted">
            {truncateName(player.name, scale < 1 ? CROWDED_RING_NAME_MAX : RING_NAME_MAX)}
          </span>
        )}
        <span aria-hidden="true" className={`text-[0.5rem] leading-none ${DOT_CLASS[connection]}`}>
          {DOT_GLYPH[connection]}
        </span>
      </span>

      {connection === 'offline' && !isMe ? (
        <OfflineFor
          since={player.lastSeenAt}
          className="text-[0.625rem] leading-tight text-ink-faint"
        />
      ) : null}

      <span className="sr-only">{label}</span>
    </div>
  );
}
